import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { removeData, setData } from 'src/storage/general.actions';
import { LeadResumeService } from './lead-resume.service';
import {
  mapLeadResumeToStore,
  checkLeadResumeAvailability,
  type LeadResumeStoreData
} from '@app/utils/lead-resume-store-mapper.util';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

/**
 * Servicio para sincronizar datos del Lead Resume con el Store de NgRx
 * Gestiona la carga y sincronización de forma centralizada
 *
 * Uso en componentes:
 * ```typescript
 * constructor(private leadResumeSyncService: LeadResumeSyncService) {}
 *
 * ngOnInit() {
 *   this.leadResumeSyncService.syncLeadResumeToStore();
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class LeadResumeSyncService {
  private readonly leadResumeService = inject(LeadResumeService);
  private readonly store = inject(Store);

  /**
   * Sincroniza TODOS los datos del Lead Resume con el Store de NgRx
   * Este método sincroniza PolicyHolder, Tenant, Property, ProductCode y leadId si están disponibles
   * @returns boolean - true si se sincronizó algún dato, false si no había datos
   */
  public syncAllLeadResumeData(): boolean {
    const currentData = this.leadResumeService.getCurrentData();

    if (!currentData) {
      console.log('ℹ️ No hay datos de Lead Resume para sincronizar');
      return false;
    }

    console.log('🔄 Sincronizando TODOS los datos del Lead Resume con Store...');
    console.log('📊 Datos crudos del Lead Resume:', currentData);

    const mappedData = mapLeadResumeToStore(currentData);
    const availability = checkLeadResumeAvailability(currentData);

    console.log('📋 Availability check:', availability);

    let syncedAny = false;

    // Sincronizar PolicyHolder
    if (availability.hasPolicyHolder && mappedData.PolicyHolder) {
      this.store.dispatch(removeData({ key: 'PolicyHolder' })); // Limpiar antes de sincronizar

      this.store.dispatch(setData({
        key: 'PolicyHolder',
        value: mappedData.PolicyHolder,
        merge: true,
      }));
      console.log('✅ PolicyHolder sincronizado', mappedData.PolicyHolder);
      syncedAny = true;
    }

    // Sincronizar Tenant
    if (availability.hasTenant && mappedData.Tenant) {
      this.store.dispatch(removeData({ key: 'Tenant' })); // Limpiar antes de sincronizar
      this.store.dispatch(setData({
        key: 'Tenant',
        value: mappedData.Tenant,
        merge: true
      }));
      console.log('✅ Tenant sincronizado', mappedData.Tenant);
      syncedAny = true;
    }

    // Sincronizar Property
    if (availability.hasProperty && mappedData.Property) {
      this.store.dispatch(removeData({ key: 'Property' })); // Limpiar antes de sincronizar
      this.store.dispatch(setData({
        key: 'Property',
        value: mappedData.Property,
        merge: true
      }));
      console.log('✅ Property sincronizado', mappedData.Property);
      syncedAny = true;
    }

    // Sincronizar ProductCode
    if (availability.hasProductCode && mappedData.ProductCode) {
      this.store.dispatch(removeData({ key: 'ProductCode' })); // Limpiar antes de sincronizar
      this.store.dispatch(setData({
        key: 'ProductCode',
        value: mappedData.ProductCode,
        merge: true
      }));
      console.log('✅ ProductCode sincronizado', mappedData.ProductCode);
      syncedAny = true;
    }

    // Sincronizar leadId
    if (currentData.leadId) {
      this.store.dispatch(removeData({ key: 'leadId' })); // Limpiar antes de sincronizar
      this.store.dispatch(setData({
        key: 'leadId',
        value: currentData.leadId,
        merge: true,
      }));
      console.log('✅ leadId sincronizado', currentData.leadId);
      syncedAny = true;
    }

    if (syncedAny) {
      console.log('✅ Lead Resume sincronizado completamente con Store');
    } else {
      console.log('⚠️ No se pudo sincronizar ningún dato del Lead Resume');
    }

    return syncedAny;
  }

  /**
   * Sincroniza los datos del Lead Resume con el Store de NgRx (método legacy)
   * @deprecated Use syncAllLeadResumeData() instead
   * @returns Observable<boolean> - true si se sincronizó, false si no había datos
   */
  public syncLeadResumeToStore(): Observable<boolean> {
    const synced = this.syncAllLeadResumeData();
    return of(synced);
  }

  /**
   * Sincroniza solo los datos del propietario (PolicyHolder)
   */
  public syncPolicyHolderOnly(): boolean {
    const currentData = this.leadResumeService.getCurrentData();

    if (!currentData) {
      console.log('ℹ️ No hay datos de Lead Resume disponibles');
      return false;
    }

    console.log('📊 Datos crudos del Lead Resume:', currentData);

    const mappedData = mapLeadResumeToStore(currentData);
    const availability = checkLeadResumeAvailability(currentData);

    console.log('📋 Availability check:', availability);
    console.log('🗺️ Mapped PolicyHolder:', mappedData.PolicyHolder);

    if (availability.hasPolicyHolder && mappedData.PolicyHolder) {
      this.store.dispatch(removeData({ key: 'PolicyHolder' })); // Limpiar antes de sincronizar
      this.store.dispatch(setData({
        key: 'PolicyHolder',
        value: mappedData.PolicyHolder,
        merge: true,
      }));
      console.log('✅ PolicyHolder sincronizado', mappedData.PolicyHolder);
      return true;
    }

    console.log('⚠️ PolicyHolder no tiene datos válidos para sincronizar');
    console.log('   - hasPolicyHolder:', availability.hasPolicyHolder);
    console.log('   - mappedData.PolicyHolder exists:', !!mappedData.PolicyHolder);
    return false;
  }

  /**
   * Sincroniza solo los datos del inquilino (Tenant)
   */
  public syncTenantOnly(): boolean {
    const currentData = this.leadResumeService.getCurrentData();

    if (!currentData) {
      console.log('ℹ️ No hay datos de Lead Resume disponibles');
      return false;
    }

    const mappedData = mapLeadResumeToStore(currentData);
    const availability = checkLeadResumeAvailability(currentData);

    console.log('📋 Tenant availability check:', availability.hasTenant);
    console.log('🗺️ Mapped Tenant:', mappedData.Tenant);

    if (availability.hasTenant && mappedData.Tenant) {
      this.store.dispatch(removeData({ key: 'Tenant' })); // Limpiar antes de sincronizar
      this.store.dispatch(setData({
        key: 'Tenant',
        value: mappedData.Tenant,
        merge: true
      }));
      console.log('✅ Tenant sincronizado', mappedData.Tenant);
      return true;
    }

    console.log('⚠️ Tenant no tiene datos válidos para sincronizar');
    return false;
  }

  /**
   * Sincroniza solo los datos de la propiedad (Property)
   */
  public syncPropertyOnly(): boolean {
    const currentData = this.leadResumeService.getCurrentData();

    if (!currentData) {
      console.log('ℹ️ No hay datos de Lead Resume disponibles');
      return false;
    }

    const mappedData = mapLeadResumeToStore(currentData);
    const availability = checkLeadResumeAvailability(currentData);

    console.log('📋 Property availability check:', availability.hasProperty);
    console.log('🗺️ Mapped Property:', mappedData.Property);

    if (availability.hasProperty && mappedData.Property) {
      this.store.dispatch(removeData({ key: 'Property' })); // Limpiar antes de sincronizar
      this.store.dispatch(setData({
        key: 'Property',
        value: mappedData.Property,
        merge: true
      }));
      console.log('✅ Property sincronizado', mappedData.Property);
      return true;
    }

    console.log('⚠️ Property no tiene datos válidos para sincronizar');
    return false;
  }

  /**
   * Obtiene los datos mapeados sin sincronizar con el store
   * Útil para validaciones o prellenado de formularios
   */
  public getMappedData(): LeadResumeStoreData | null {
    const currentData = this.leadResumeService.getCurrentData();

    if (!currentData) {
      return null;
    }

    return mapLeadResumeToStore(currentData);
  }

  /**
   * Verifica si hay datos específicos disponibles
   */
  public hasData(dataType: 'PolicyHolder' | 'Tenant' | 'Property' | 'ProductCode'): boolean {
    const currentData = this.leadResumeService.getCurrentData();

    if (!currentData) {
      return false;
    }

    const availability = checkLeadResumeAvailability(currentData);

    switch (dataType) {
      case 'PolicyHolder':
        return availability.hasPolicyHolder;
      case 'Tenant':
        return availability.hasTenant;
      case 'Property':
        return availability.hasProperty;
      case 'ProductCode':
        return availability.hasProductCode;
      default:
        return false;
    }
  }

  /**
   * Despacha todas las acciones al store
   * @private
   */
  private dispatchToStore(data: LeadResumeStoreData, availability: ReturnType<typeof checkLeadResumeAvailability>): void {
    // PolicyHolder (Propietario) - siempre debería existir
    if (availability.hasPolicyHolder && data.PolicyHolder) {
      this.store.dispatch(setData({
        key: 'PolicyHolder',
        value: data.PolicyHolder,
        merge: true
      }));
      console.log('  ✓ PolicyHolder despachado');
    }

    // Tenant (Inquilino) - opcional
    if (availability.hasTenant && data.Tenant) {
      this.store.dispatch(setData({
        key: 'Tenant',
        value: data.Tenant,
        merge: true
      }));
      console.log('  ✓ Tenant despachado');
    }

    // Property (Propiedad/Inmueble) - opcional
    if (availability.hasProperty && data.Property) {
      this.store.dispatch(setData({
        key: 'Property',
        value: data.Property,
        merge: true
      }));
      console.log('  ✓ Property despachado');
    }

    // ProductCode - opcional
    if (availability.hasProductCode && data.ProductCode) {
      this.store.dispatch(setData({
        key: 'ProductCode',
        value: data.ProductCode,
        merge: false
      }));
      console.log('  ✓ ProductCode despachado');
    }
  }

  /**
   * Limpia los datos del Lead Resume del store
   * Útil para reiniciar el flujo
   */
  public clearLeadResumeFromStore(): void {
    console.log('🧹 Limpiando Lead Resume del Store...');

    // Nota: Solo limpiamos si hay datos de Lead Resume cargados
    if (this.leadResumeService.hasLoadedData()) {
      // Aquí podrías implementar removeData actions si es necesario
      // Por ahora solo limpiamos el servicio
      this.leadResumeService.clearState();
      console.log('✅ Lead Resume limpiado');
    }
  }
}
